import { message } from 'antd';
import { FC, PropsWithChildren, useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import Loading from 'src/layout/Loading';
import MainNavigation from 'src/layout/MainNavigation';
import MenuDrawer from 'src/layout/MenuDrawer';
import { isVersionLessThan } from 'src/utils/common';
import { isLocal, isProduction, version } from 'src/utils/config';
import { hasExceededMaxAttempts } from 'src/utils/force-update-util';

import { selectUserId } from 'src/redux/slices/auth';
import { useAppSelector } from 'src/redux/store';

import { useApp } from 'src/stores/app-context';

import GlobalNoticeBanner from 'src/components/atoms/GlobalNoticeBanner/GlobalNoticeBanner';
import ForceUpdateModal from 'src/components/atoms/Modal/ForceUpdateModal';
import OrderButtons from 'src/components/molecules/OrderButtons/OrderButtons';
import OrderCard from 'src/components/molecules/OrderCard/OrderCard';

const mainStyle = {
  maxWidth: '540px',
  margin: '0 auto',
  backgroundColor: 'white',
};

type LayoutProps = {
  isLoggedIn: boolean;
  isLoading: boolean;
};

const isShowOrderPannel = (pathname: string) =>
  [
    '/home',
    '/favorite',
    '/shopping-cart',
    '/product',
    '/category',
    '/search/result',
    '/store-category',
    '/promotion',
    '/custom-promotion',
  ].some((path) => pathname.startsWith(path));

const isOrderPannelBottom = (pathname: string) =>
  ['/product', '/category', '/search/result', '/store-category', '/custom-promotion', '/promotion'].some((path) =>
    pathname.startsWith(path)
  );

const isShowNavigation = (pathname: string) =>
  ['/home', '/favorite', '/shopping-cart'].some((path) => pathname.startsWith(path));

const Layout: FC<PropsWithChildren<LayoutProps>> = ({ children, isLoggedIn, isLoading }) => {
  const userId = useAppSelector(selectUserId);
  const { pathname } = useLocation();
  const { showLoading, commerceConf } = useApp();
  const [showForceUpdate, setShowForceUpdate] = useState(false);

  const hideOrderCard = pathname.startsWith('/shopping-cart');
  const showNavigation = isShowNavigation(pathname);
  const showOrderPannel = userId && isShowOrderPannel(pathname);
  const orderPannelBottom = isOrderPannelBottom(pathname);

  // 버전 체크
  useEffect(() => {
    const commerceConfWithMinVersion = commerceConf;
    const minVersion = commerceConfWithMinVersion?.minVersion;

    if (minVersion && isVersionLessThan(version, minVersion)) {
      // 5회 초과 시 강제 업데이트 모달을 더 이상 보여주지 않고 에러 메시지 표시
      if (hasExceededMaxAttempts()) {
        message.error('최신 버전 업데이트에 실패했습니다. 고객센터에 문의해주세요.');
        setShowForceUpdate(false);
        return;
      }

      setShowForceUpdate(true);
    } else {
      setShowForceUpdate(false);
    }
  }, [commerceConf]);

  return (
    <>
      {isProduction ? null : (
        <div style={{ position: 'fixed', top: 0, left: 0, zIndex: 1000 }}>👾{isLocal ? ' 💻' : ''}</div>
      )}
      <main id='main-div' style={mainStyle}>
        {/* 전역 공지는 메인 탭(홈/즐겨찾기/장바구니·주문목록)에서만 노출한다. */}
        {showNavigation && <GlobalNoticeBanner />}
        {children}
      </main>
      {showOrderPannel && (
        <>
          <OrderButtons stickToBottom={orderPannelBottom} />
          {!hideOrderCard && <OrderCard y={orderPannelBottom ? 46 : undefined} />}
        </>
      )}
      {showNavigation && <MainNavigation />}
      <MenuDrawer isLoggedIn={isLoggedIn} />
      {(showLoading || isLoading) && <Loading fontSize={56} />}
      <ForceUpdateModal isOpen={showForceUpdate} />
    </>
  );
};

export default Layout;
